// JanScanner.js
import React, { useEffect, useRef } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";

export default function JanScanner({ onDetected, onFallbackManual, isAU = false }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const readerRef = useRef(null);

  useEffect(() => {
    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;

    let alive = true;

    (async () => {
      try {
        const videoEl = videoRef.current;
        if (!videoEl) return;

        // iOS/Safari 対策：属性を念押し（JSXのplaysInlineだけだと効かない端末がある）
        try {
          videoEl.setAttribute("playsinline", "true");
          videoEl.setAttribute("webkit-playsinline", "true");
        } catch {}

        // ✅ 背面カメラ優先（スマホ安定）
        const constraints = {
          audio: false,
          video: {
            facingMode: { ideal: "environment" },
            // 端末によっては効く：解像度の理想値（無理なら自動で落ちる）
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        };

        const controls = await reader.decodeFromConstraints(
          constraints,
          videoEl,
          (result, err) => {
            if (!alive) return;

            if (result) {
              const text = result.getText?.() ?? String(result);

              try {
                controlsRef.current?.stop();
              } catch {}

              onDetected(text);
            }
          }
        );

        controlsRef.current = controls;
      } catch (e) {
        console.error("カメラ起動失敗", e);

        // ✅ 何が起きてるか分かるように詳細も出す
        const name = e?.name || "";
        const msg = e?.message || String(e);
        alert(isAU
          ? `Could not start the camera (${name})\n${msg}\n\nSwitching to manual entry.`
          : `カメラを起動できません（${name}）\n${msg}\n\n手入力に切り替えます。`);

        onFallbackManual?.();
      }
    })();

    return () => {
      alive = false;

      // stop() でストリーム停止（念のため）
      try {
        controlsRef.current?.stop();
      } catch {}

      // videoのsrcObjectも念のためクリア
      try {
        if (videoRef.current) videoRef.current.srcObject = null;
      } catch {}
    };
  }, [onDetected, onFallbackManual, isAU]);

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div style={{ fontWeight: 900 }}>{isAU ? "Scan barcode" : "JANをスキャン"}</div>

      <div style={{ position: "relative", width: "100%", aspectRatio: "16/9", borderRadius: 12, overflow: "hidden", background: "#000" }}>
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          style={{
            position: "absolute",
            top: 0, left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
        {/* スキャン補助ライン */}
        <div style={{
          position: "absolute",
          top: "50%", left: "10%", right: "10%",
          height: 2,
          background: "rgba(255, 80, 80, 0.8)",
          transform: "translateY(-50%)",
          boxShadow: "0 0 8px rgba(255,80,80,0.6)",
        }} />
      </div>

      <div style={{ fontSize: 12, opacity: 0.8 }}>
        {isAU ? "* Hold the camera over the barcode" : "※ カメラをJANコードにかざしてください"}
      </div>

      <button
        onClick={onFallbackManual}
        style={{ padding: 10, borderRadius: 10, fontWeight: 900 }}
      >
        {isAU ? "⌨️ Switch to manual entry" : "⌨️ 手入力に切り替える"}
      </button>
    </div>
  );
}
